import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import ResourcesBreakdown from "./ResourcesBreakdown";
import ProjectInfo from "./ProjectInfo";
import InvoiceTab from "./InvoiceTab";
import "./estimate.css";
import { motion } from "framer-motion";
import {
  FileText,
  ListChecks,
  CreditCard,
  ArrowLeft,
  X,
  CheckCircle2,
} from "lucide-react";

const CreateEstimate = () => {
  const navigate = useNavigate();
  const { companyId } = useCompany(); // ← pull in your company context
  const [currentTab, setCurrentTab] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [estimateData, setEstimateData] = useState({
    customerName: "",
    customerAddress: "",
    date: new Date().toISOString().split("T")[0],
    expiryDate: "",
    estimateNumber: `EST-${Math.floor(1000 + Math.random() * 9000)}`,
    projectName: "",
    projectDuration: "",
    projectManager: "",
    projectType: "",
    resources: [],
  });

  // 🚧 Don’t try to load customers until we know our companyId (UNCHANGED)
  useEffect(() => {
    if (!companyId) return;

    const fetchCustomers = async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("id, name, address, company_name")
        .eq("company_id", companyId);

      if (error) {
        console.error("Error fetching customers:", error);
      } else {
        setCustomers(data || []);
      }
    };

    fetchCustomers();
  }, [companyId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEstimateData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCustomerChange = (e) => {
    const selectedId = e.target.value;
    const cust = customers.find((c) => String(c.id) === String(selectedId));
    if (cust) {
      setEstimateData((prev) => ({
        ...prev,
        customerName: cust.company_name,
        customerAddress: cust.address,
      }));
    }
  };

  const nextTab = () => {
    if (currentTab === 1) {
      if (
        !estimateData.customerName ||
        !estimateData.expiryDate ||
        !estimateData.projectDuration
      ) {
        return alert("Please fill in all required fields.");
      }
    }
    setCurrentTab((t) => t + 1);
  };

  const prevTab = () => setCurrentTab((t) => t - 1);
  const cancel = () => navigate("/dashboard");

  const steps = [
    { id: 1, label: "Project Info", icon: FileText },
    { id: 2, label: "Resources", icon: ListChecks },
    { id: 3, label: "Invoice", icon: CreditCard },
  ];

  const progress = (currentTab / steps.length) * 100;

  return (
    <div className="estimate-container">
      {/* Sticky header with actions */}
      <header className="est-head panel">
        <div className="est-left">
          <button className="icon-btn ghost" onClick={cancel} title="Back">
            <ArrowLeft size={16} />
          </button>
          <div>
            <h2>Create Estimate</h2>
            <p className="muted">
              {estimateData.estimateNumber} • {estimateData.date}
            </p>
          </div>
        </div>

        <div className="est-actions">
          {currentTab > 1 && (
            <button className="btn-ghost" onClick={prevTab}>
              Back
            </button>
          )}
          {currentTab < 3 ? (
            <button className="btn-primary" onClick={nextTab}>
              Continue
            </button>
          ) : (
            <button className="btn-primary" onClick={() => setCurrentTab(3)}>
              <CheckCircle2 size={16} /> Review & Finish
            </button>
          )}
          <button className="icon-btn" onClick={cancel} title="Cancel">
            <X size={16} />
          </button>
        </div>
      </header>

      {/* Progress Stepper (keeps legacy hook ids for safety) */}
      <section id="progress-tabs" className="est-stepper panel">
        <div className="stepper-track" aria-hidden="true">
          <div className="stepper-progress" style={{ width: `${progress}%` }} />
        </div>

        <div className="steps">
          {steps.map(({ id, label, icon: Icon }) => {
            const active = currentTab >= id;
            const clickable =
              id < currentTab ||
              id === currentTab ||
              (id === 2 && currentTab >= 2) ||
              (id === 3 && currentTab >= 3);
            return (
              <button
                key={id}
                id="tab1-btn"
                className={`step ${active ? "active" : ""}`}
                onClick={clickable ? () => setCurrentTab(id) : undefined}
                aria-current={currentTab === id ? "step" : undefined}
              >
                <span className={`step-icon ${active ? "on" : ""}`}>
                  <Icon size={16} />
                </span>
                <span className="step-label">{label}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Content shell */}
      <motion.section
        className="est-body panel"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {currentTab === 1 && (
          <ProjectInfo
            customers={customers}
            estimateData={estimateData}
            handleCustomerChange={handleCustomerChange}
            handleInputChange={handleInputChange}
            nextTab={nextTab}
            prevTab={prevTab}
            cancel={cancel}
          />
        )}

        {currentTab === 2 && (
          <ResourcesBreakdown
            resources={estimateData.resources}
            setResources={(resources) =>
              setEstimateData((prev) => ({ ...prev, resources }))
            }
            setCurrentTab={setCurrentTab}
            estimateData={estimateData}
            setEstimateData={setEstimateData}
          />
        )}

        {currentTab === 3 && (
          <InvoiceTab
            estimateData={estimateData}
            setEstimateData={setEstimateData}
            setCurrentTab={setCurrentTab}
          />
        )}
      </motion.section>
    </div>
  );
};

export default CreateEstimate;
